import React from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Mail, Trash2, AlertTriangle } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useWorkflowStore } from '../store';
import { NodeActionPopover } from './NodeActionPopover';
import { AgentSummary } from '../api';

/** A step that sends an email instead of placing a call.
 *
 *  It fires on the same edge conditions that route the next call — the edge
 *  into this node carries the outfield clause. The template is edited in the
 *  inspector; this card only summarises it and flags an unfinished one, since
 *  the server refuses to publish a workflow containing one.
 */
export function EmailNode({ id, data, selected, positionAbsoluteX, positionAbsoluteY }: NodeProps) {
  const setNodes = useWorkflowStore(state => state.setNodes);
  const setEdges = useWorkflowStore(state => state.setEdges);
  const nodes = useWorkflowStore(state => state.nodes);
  const edges = useWorkflowStore(state => state.edges);
  const validationErrors = useWorkflowStore(state => state.validationErrors);

  const email = ((data as any)?.email as any) || {};
  const to = String(email.to || "").trim();
  const subject = String(email.subject || "").trim();
  const body = String(email.body || "").trim();
  const incomplete = !to || !subject || !body;
  const error = validationErrors?.[id];

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNodes(nodes.filter(n => n.id !== id));
    setEdges(edges.filter(e => e.source !== id && e.target !== id));
  };

  return (
    <div
      className={cn(
        "group relative flex flex-col rounded-xl border bg-elevated text-left shadow-sm hover:shadow-md transition-all duration-200",
        error ? "border-danger ring-2 ring-danger/20"
          : selected ? "border-brand ring-2 ring-brand/20"
          : "border-line",
      )}
      style={{ width: 280 }}
    >
      <Handle type="target" position={Position.Left} className="w-1 h-1 opacity-0" />

      <div className="flex items-center justify-between px-3.5 py-3">
        {/* Real channel colour for the type badge — email red. */}
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#EA4335]/40 bg-[#EA4335]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#C5221F]">
          <Mail className="h-3 w-3" />
          Email
        </span>
        <button
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 text-ink-3 hover:text-danger transition-colors"
          title="Remove step"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="flex flex-col gap-1.5 px-3.5 pb-3">
        <div className="text-[13px] font-bold text-ink truncate">
          {String((data as any)?.nodeLabel || "Send Email")}
        </div>
        {incomplete ? (
          <span className="flex items-center gap-1.5 text-[11px] font-semibold text-warning">
            <AlertTriangle className="h-3.5 w-3.5" />
            Template incomplete — cannot publish
          </span>
        ) : (
          <>
            <span className="truncate text-[11px] text-ink-3">
              <span className="text-ink-3">To </span>{to}
            </span>
            <span className="truncate text-[11px] font-medium text-ink-2">{subject}</span>
          </>
        )}
      </div>

      <NodeActionPopover
        nodeId={id}
        positionX={positionAbsoluteX || 0}
        positionY={positionAbsoluteY || 0}
        availableAgents={(data as any).availableAgents as AgentSummary[]}
      />
    </div>
  );
}
